import { Navbar, Nav, Button, NavDropdown } from "react-bootstrap";
import { motion } from "framer-motion";
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "../assets/logo-light.png";
import LogoDark from "../assets/logo-dark.png";

const NavbarSection = ({ textColor = "white" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // --- CUSTOM CSS FOR PROFESSIONAL LOOK ---
  const customStyles = `
    /* Force the dropdown toggle text color to match the prop (White/Black) */
    .custom-navbar .nav-link.dropdown-toggle {
      color: ${textColor} !important;
      font-weight: 500;
      display: flex;
      align-items: center;
    }

    /* Remove the default Bootstrap arrow if you want a cleaner look, 
       or keep it. This centers it nicely. */
    .custom-navbar .dropdown-toggle::after {
      margin-left: 6px;
      vertical-align: middle;
    }

    /* The Dropdown Box itself */
    .custom-navbar .dropdown-menu {
      background-color: #ffffff;
      border: none;
      border-radius: 12px;
      box-shadow: 0 10px 40px -10px rgba(0,0,0,0.15);
      padding: 8px;
      margin-top: 15px; /* Spacing from navbar */
      min-width: 200px;
      animation: slideUp 0.2s ease-out;
    }

    /* Dropdown Items */
    .custom-navbar .dropdown-item {
      color: #1a1a1a;
      font-size: 14px;
      font-weight: 500;
      padding: 10px 16px;
      border-radius: 8px;
      transition: all 0.2s ease;
      background: transparent;
    }

    /* Hover Effect for Items */
    .custom-navbar .dropdown-item:hover,
    .custom-navbar .dropdown-item:focus,
    .custom-navbar .dropdown-item:active {
      background-color: #f3f4f6; /* Light Gray Hover */
      color: #000;
      transform: translateX(4px); /* Subtle slide movement */
    }

    /* Animation */
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;

  const handleScrollClick = (e, target) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.querySelector(target);
        if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      const element = document.querySelector(target);
      if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setIsOpen(false);
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getButtonStyles = () => {
    const isWhite = textColor === "white";
    return {
      backgroundColor: isButtonHovered ? (isWhite ? "#f0f0f0" : "#2a2a3e") : (isWhite ? "#fff" : "#1a1a2e"),
      color: isWhite ? "#000" : "#fff",
      border: "none",
      fontWeight: "600",
      transition: "all 0.3s ease",
      padding: "8px 24px",
      borderRadius: "8px"
    };
  };

  const logoSrc = textColor === "white" ? Logo : LogoDark;

  return (
    <>
      <style>{customStyles}</style>
      <Navbar 
        expand="lg" 
        className="custom-navbar" 
        expanded={isOpen}
      >
        <div className="container">
          {/* Logo */}
          <Navbar.Brand
            as={motion.a}
            href="/"
            onClick={handleLogoClick}
            className="d-flex align-items-center"
            style={{ cursor: "pointer" }}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img
              src={logoSrc}
              alt="WriteOffGenie Logo"
              style={{ width: "40px", height: "40px", marginRight: "10px", objectFit: "contain" }}
            />
            <span style={{ color: textColor, fontSize: "1.3rem", fontWeight: "400", letterSpacing: "-0.5px" }}>
              WriteOffGenie
            </span>
          </Navbar.Brand>

          {/* Mobile Toggle */}
          <Navbar.Toggle
            as={motion.button}
            onClick={() => setIsOpen(!isOpen)}
            className="custom-toggle"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 10 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className={`hamburger ${isOpen ? "open" : ""}`}>
              <span style={{ backgroundColor: textColor }}></span>
              <span style={{ backgroundColor: textColor }}></span>
              <span style={{ backgroundColor: textColor }}></span>
            </div>
          </Navbar.Toggle>

          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-end">
            <Nav className="align-items-center gap-4">
              
              {/* Features */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Nav.Link
                  href="#features"
                  className="nav-link-item"
                  style={{ color: textColor, fontWeight: 500 }}
                  onClick={(e) => handleScrollClick(e, "#features")}
                >
                  Features
                </Nav.Link>
              </motion.div>

              {/* Support */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Nav.Link
                  as={Link}
                  to="/support"
                  className="nav-link-item"
                  style={{ color: textColor, fontWeight: 500 }}
                  onClick={() => setIsOpen(false)}
                >
                  Support
                </Nav.Link>
              </motion.div>

              {/* Partner Dropdown */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.35 }}
              >
                <NavDropdown
                  title="Partner"
                  id="partner-dropdown"
                  // Render custom classes to pick up the CSS above
                >
                  <NavDropdown.Item 
                    as={Link} 
                    to="/cpa" 
                    onClick={() => setIsOpen(false)}
                  >
                    CPA
                  </NavDropdown.Item>
                  <NavDropdown.Item 
                    href="https://partners.writeoffgenie.ai" 
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                  >
                    Partner Login
                  </NavDropdown.Item>
                </NavDropdown>
              </motion.div>

              {/* Download Button */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Button
                  size="sm"
                  className="download-btn shadow-sm"
                  style={getButtonStyles()}
                  onMouseEnter={() => setIsButtonHovered(true)}
                  onMouseLeave={() => setIsButtonHovered(false)}
                  onClick={() => {
                    navigate("/download");
                    setIsOpen(false);
                  }}
                >
                  Download App
                </Button>
              </motion.div>
            </Nav>
          </Navbar.Collapse>
        </div>
      </Navbar>
    </>
  );
};

export default NavbarSection;
